// SetupChecklist — "Finish setting up" card for the Connections page.
//
// A live getting-started guide for a freshly-deployed client: are the workers
// running, is a meeting source connected, Gmail+Calendar, and is the Mac
// bridge capturing. Reads the connections map (passed in) + daemon heartbeats.
// Auto-hides once everything is done so it never nags a fully-set-up account.

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, Circle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { readAllHeartbeats, classifyHeartbeat } from '@reverra/shared/heartbeat/read';
import GlassCard from '@/components/shared/GlassCard';
import { supabase } from '@/lib/supabase';

type Props = { connectedProviders: Set<string> };

type Item = { done: boolean; label: string; to: string | null; hereBelow?: boolean };

export default function SetupChecklist({ connectedProviders }: Props): JSX.Element | null {
  const [now, setNow] = useState<Date>(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(id);
  }, []);

  const hbQ = useQuery({
    queryKey: ['heartbeats'],
    queryFn: () => readAllHeartbeats(supabase),
    refetchInterval: 30_000,
    retry: 0,
  });

  const rows = hbQ.data ?? [];
  const aliveNames = new Set(
    rows
      .filter((r) => r.payload && classifyHeartbeat(r.payload, now).status === 'alive')
      .map((r) => r.daemon_name),
  );
  const workersRunning = [...aliveNames].some((n) => n.startsWith('workers_'));
  const bridgeAlive = aliveNames.has('bridge_imessage');

  const items: Item[] = [
    { done: workersRunning, label: 'Capture engine running', to: '/agents' },
    {
      done: connectedProviders.has('fathom'),
      label: 'Connect a meeting source (Fathom)',
      to: null,
      hereBelow: true,
    },
    {
      done: connectedProviders.has('google'),
      label: 'Connect Gmail & Calendar',
      to: null,
      hereBelow: true,
    },
    { done: bridgeAlive, label: 'Capture from your Mac (iMessage / WhatsApp)', to: '/settings' },
  ];
  const doneCount = items.filter((i) => i.done).length;

  // Don't render until heartbeats have loaded once (avoids a flash of all-incomplete),
  // and hide entirely once everything is set up.
  if (hbQ.isLoading) return null;
  if (doneCount === items.length) return null;

  return (
    <GlassCard hover={false} className="mb-4">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-sm font-semibold text-foreground">Finish setting up</h2>
        <span className="text-[11px] text-muted-foreground">
          {doneCount} of {items.length} done
        </span>
      </div>
      <ul className="space-y-1.5">
        {items.map((it, i) => (
          <li key={i} className="flex items-center gap-2 text-xs">
            {it.done ? (
              <Check className="w-3.5 h-3.5 text-primary shrink-0" />
            ) : (
              <Circle className="w-3.5 h-3.5 text-muted-foreground/40 shrink-0" />
            )}
            <span className={it.done ? 'text-muted-foreground line-through' : 'text-foreground'}>
              {it.label}
            </span>
            {!it.done && it.to && (
              <Link
                to={it.to}
                className="ml-auto inline-flex items-center gap-0.5 text-[11px] text-primary hover:underline"
              >
                Go <ArrowRight className="w-3 h-3" />
              </Link>
            )}
            {!it.done && !it.to && it.hereBelow && (
              <span className="ml-auto text-[11px] text-muted-foreground">below ↓</span>
            )}
          </li>
        ))}
      </ul>
    </GlassCard>
  );
}
